import React from 'react';
import { Button, Form, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CUSTOM_MARKER_OWN_SECTION_LABEL,
  CustomMarkerSection,
  practitionerSectionFromOptionValue,
  practitionerSectionKey,
  practitionerSectionProblem,
} from '../../models';
import { moveRowWithin } from '../bloodworkz/custom-markers-table';
import '../bloodworkz/custom-markers-table.css';

/**
 * Validation for the section list, keyed by section id so each message renders
 * against the row it is about. Save holds a section that does not validate back
 * and commits the rest of the page around it, so the message has to be able to
 * find its row. The messages are self-contained (they name the section)
 * because nothing resolves a label for these keys.
 */
export function sectionProblemsById(
  sections: CustomMarkerSection[],
): { [sectionId: string]: string } {
  const byId: { [sectionId: string]: string } = {};
  const list = sections || [];
  list.forEach((section, index) => {
    if (!(section.name || '').trim()) {
      return;
    }
    // Only the sections ABOVE this one, so a repeated name is reported against
    // the row that repeated it rather than against both. Checked against the
    // whole list, typing a name a section already has put an identical error on
    // the section that was there first and correct, and held it back from
    // saving along with the duplicate. This is also how the marker list reads a
    // name already taken (see duplicateNameProblems).
    const problem = practitionerSectionProblem(
      section.name,
      list.slice(0, index),
      section.id,
    );
    if (problem) {
      byId[section.id] = problem;
    }
  });
  return byId;
}

/**
 * Correct a section's name in the list, matched on its current name. Adds it
 * when it is not there — a name typed on a test but never saved to the list is
 * still a section the practitioner is using.
 */
export function renameInSections(
  sections: CustomMarkerSection[],
  from: string,
  to: string,
  newId: () => string,
): CustomMarkerSection[] {
  const trimmed = (to || '').trim();
  if (!trimmed) {
    return sections;
  }
  const fromKey = practitionerSectionKey(from);
  const existing = sections || [];
  const match = existing.find(
    section => practitionerSectionKey(section.name) === fromKey,
  );
  if (!match) {
    return [...existing, new CustomMarkerSection({ id: newId(), name: trimmed })];
  }
  return existing.map(section =>
    section === match
      ? new CustomMarkerSection({ id: section.id, name: trimmed })
      : section,
  );
}

/** Blank rows are dropped rather than saved as nameless sections. */
export function sectionsFromRows(
  sections: CustomMarkerSection[],
): CustomMarkerSection[] {
  return (sections || [])
    .filter(section => (section.name || '').trim())
    .map(
      section =>
        new CustomMarkerSection({
          id: section.id,
          name: section.name.trim(),
        }),
    );
}

/**
 * Whether some OTHER entry in the list is called this too.
 *
 * A marker stores the NAME it was filed under, never a reference to a section
 * record, so two entries sharing a name are ONE section as far as every marker
 * is concerned. While that is true, which of the two a marker belongs to is not
 * a question the list can answer, so nothing may move those markers at all:
 * acting on a guess disturbs a section the practitioner did not touch. Removing
 * one of the two leaves the markers alone for this reason, and so does renaming
 * one (see MyMarkersContainer.handleSectionsChange).
 *
 * A blank name holds nothing. It matches every marker sitting in Other Lab
 * Results, which is not one of these sections at all.
 */
export function sectionNameHeldElsewhere(
  sections: CustomMarkerSection[],
  sectionId: string,
  name?: string,
): boolean {
  const key = practitionerSectionKey(name);
  return (
    !!key &&
    (sections || []).some(
      section =>
        section.id !== sectionId && practitionerSectionKey(section.name) === key,
    )
  );
}

/**
 * How many library markers are filed under each section, keyed by section id.
 *
 * Supplied to this component rather than worked out inside it: it holds the
 * section list, and the markers filed under a section live a card away on the
 * page that owns both. Matched the way every other section comparison is, on
 * the normalized name, because a marker stores the NAME it was filed under and
 * never a reference to the section record.
 *
 * A section still being named counts nothing. Its key is blank, and a blank key
 * matches every marker left in Other Lab Results, which would put a
 * "12 markers" warning on an empty row nobody has finished typing.
 */
export function markerCountsBySection(
  sections: CustomMarkerSection[],
  rows: { section: string }[],
): { [sectionId: string]: number } {
  const counts: { [sectionId: string]: number } = {};
  // A name typed twice is still ONE section to every marker filed under it, so
  // the markers are counted against the first entry and the duplicate reads
  // zero. Counting them twice put the same "12 markers" warning on both rows
  // and made the copy look like a panel of its own worth keeping.
  const claimed = new Set<string>();
  (sections || []).forEach(section => {
    const key = practitionerSectionKey(section.name);
    counts[section.id] =
      key && !claimed.has(key)
        ? (rows || []).filter(
            row =>
              practitionerSectionKey(
                practitionerSectionFromOptionValue(row.section),
              ) === key,
          ).length
        : 0;
    if (key) {
      claimed.add(key);
    }
  });
  return counts;
}

type Props = {
  sections: CustomMarkerSection[];
  onChange: (sections: CustomMarkerSection[]) => void;
  /** Supplied by the caller so this component stays free of id generation. */
  newSectionId: () => string;
  /** What Save found wrong, keyed by section id, rendered under its row. */
  errors?: { [sectionId: string]: string };
  /** Markers filed under each section, keyed by section id. */
  markerCounts?: { [sectionId: string]: number };
  /**
   * Remove a section and say what becomes of the markers filed under it. The
   * disposition means nothing for a section nobody has filed anything under,
   * which removes on the click and passes 'move'.
   */
  onRemove: (section: CustomMarkerSection, markers: 'move' | 'delete') => void;
};

/**
 * The ⓘ beside "My Sections", matching the one beside "My Markers".
 *
 * Carries what is read once and never again: what ordering the list does, and
 * what a rename leaves alone. What a section of your own IS stays on the page,
 * the way it does for markers — a card whose title is the only thing above its
 * list says nothing about why the list is there, and that sentence is the one
 * somebody arriving needs. Everything after it is detail, and detail standing
 * above five text boxes is detail between the practitioner and the row they
 * came to rename.
 *
 * stopPropagation because the card header this sits in toggles the card.
 */
export const CustomMarkerSectionsHeader: React.FC = () => (
  <OverlayTrigger
    trigger="click"
    rootClose
    placement="bottom-start"
    overlay={
      <Popover id="my-sections-help" className="custom-marker-help-popover">
        <Popover.Title as="h6">How My Sections works</Popover.Title>
        <Popover.Content>
          <p>
            <strong>Drag a section by the handle at its left</strong> to order
            them, or select a handle and use the up and down arrow keys. That
            order carries through to the marker list above, to the Section list
            on Add Test Results, and to the order your sections print in on the
            analysis, the comparison table and their PDFs.
          </p>
          <p>
            Renaming or removing one here leaves tests you have already saved
            exactly as they were, and nothing is written until you save the
            page.
          </p>
        </Popover.Content>
      </Popover>
    }
  >
    <span
      className="custom-marker-block-help"
      title="How My Sections works"
      onClick={(e: any) => e.stopPropagation()}
    >
      <FontAwesomeIcon icon="info-circle" />
    </span>
  </OverlayTrigger>
);

/**
 * "My Sections" — report sections the practitioner named themselves, for panels
 * LabSmarts has no home for.
 *
 * Sits BELOW the marker list. It led the page while a section had to exist
 * before a marker could be filed into it, but the Section dropdown creates them
 * inline now, on this page and while entering a test — so nobody comes here to
 * set one up, only to rename or remove one. Setup work goes where it is needed;
 * what is left is maintenance, and maintenance does not get the top of a page
 * named for something else.
 *
 * THE ORDER OF THIS LIST IS THE ORDER THE SECTIONS ARE GROUPED IN, here and on
 * Add Test Results — see groupBySectionValue, which ranks the practitioner's
 * own sections by their slot in it. That is why the rows drag: the list was
 * append-only while nothing read its order, and the panel a practitioner works
 * in first is not the one they happened to create first.
 */
export const CustomMarkerSections: React.FC<Props> = ({
  sections,
  onChange,
  newSectionId,
  errors,
  markerCounts,
  onRemove,
}) => {
  const rows = sections || [];
  const counts = markerCounts || {};
  /*
   * What to say under each row: the rules checked as the name is TYPED, plus
   * anything the last Save added on top.
   *
   * A name a section already has was only ever reported by Save, which is a
   * long way from where it is made — the list showed two identical rows and
   * said nothing until the button was pressed, and by then the practitioner had
   * moved on to the markers. Every rule here is about the one field this row
   * has, and a field with something in it can be judged, so there is nothing to
   * hold until Save the way a half-filled range on a marker has to be.
   *
   * Save's messages last, so a row it refused keeps that message until it is
   * edited (handleSectionsChange clears it), and the two never stack up: they
   * are the same rules in the same words.
   */
  const problems = { ...sectionProblemsById(rows), ...(errors || {}) };
  // The section the trash was pressed on while markers were still filed under
  // it. Removing it silently is what made the trash look broken: the heading is
  // built from the markers, so it stayed on the page with its markers under it
  // and only the entry in this list disappeared.
  const [pending, setPending] = React.useState<CustomMarkerSection | undefined>(
    undefined,
  );
  const pendingCount = pending ? counts[pending.id] || 0 : 0;
  const pendingName = (pending?.name || '').trim();
  // The row being dragged by its handle, and the row it is currently over.
  const [dragIndex, setDragIndex] = React.useState<number | undefined>(
    undefined,
  );
  const [dropIndex, setDropIndex] = React.useState<number | undefined>(
    undefined,
  );
  const clearDrag = () => {
    setDragIndex(undefined);
    setDropIndex(undefined);
  };
  // Every row is a slot, unlike the marker list where a section's rows are
  // scattered through one array and only its own slots may be permuted. Shared
  // with it anyway rather than splicing here: one place to get the arithmetic
  // right, and it already returns the SAME array when nothing moved.
  const slots = rows.map((_section, index) => index);

  const move = (fromIndex: number, toIndex: number) => {
    const next = moveRowWithin(rows, slots, fromIndex, toIndex);
    if (next !== rows) {
      onChange(next);
    }
  };

  const moveBy = (index: number, step: number) => {
    const target = index + step;
    if (target < 0 || target >= rows.length) {
      return;
    }
    move(index, target);
  };

  /**
   * Where the dragged row would land, drawn on the row it would land against.
   *
   * The marker list's classes, because it is the same drawing: an inset rather
   * than a border, so the row the practitioner is aiming at does not change
   * height and slide out from under the pointer as it is crossed.
   */
  const dropClass = (index: number): string => {
    if (dragIndex === undefined) {
      return '';
    }
    if (dragIndex === index) {
      return 'custom-marker-line-dragging';
    }
    if (dropIndex !== index) {
      return '';
    }
    return dragIndex < index
      ? 'custom-marker-line-drop-below'
      : 'custom-marker-line-drop-above';
  };

  const update = (index: number, name: string) => {
    onChange(
      rows.map((section, i) =>
        i === index ? new CustomMarkerSection({ ...section, name }) : section,
      ),
    );
  };

  return (
    <div>
      {/* What these are, and nothing else. Ordering, the arrow keys and what a
          rename leaves alone are all in the ⓘ on the card's header
          (CustomMarkerSectionsHeader): this is the sentence somebody arriving
          needs, and the rest is read once and then stands between them and the
          row they came to rename. Same two sentences, same style, as the line
          above the marker list.

          It named "the Section list wherever you add a marker", which is a
          dropdown and two screens described as neither. A practitioner cannot
          act on a control they cannot find, so both are now said outright: the
          Section dropdown, on this page and on Add Test Results.

          "You add yourself" rather than "custom", and rather than nothing at
          all: a LabSmarts marker on Add Test Results has no Section dropdown,
          so an unqualified "every new marker" promises a control that is not
          on most of that screen's rows. The words are the card's own — it is
          headed "Add Your Own Markers to This Test" — and "custom" is a term
          this product says to practitioners in one place only, the paywall. */}
      <p style={{ fontSize: '13px', color: '#212529', marginBottom: '10px' }}>
        Sections of your own, for panels LabSmarts does not cover yet (for
        example, a Urinalysis panel). They join the LabSmarts sections in the{' '}
        <strong>Section</strong> dropdown on every new marker you add yourself,
        here and in Add Test Results, and print after them under the name you
        give them.
      </p>
      {rows.length ? (
        <div>
          {rows.map((section, index) => {
            const problem = problems[section.id];
            return (
              <div
                key={section.id}
                id={`my-section-row-${section.id}`}
                className={dropClass(index)}
                style={{ marginBottom: '6px' }}
                onDragOver={e => {
                  if (dragIndex === undefined) {
                    return;
                  }
                  // Without this the drop never fires.
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dropIndex !== index) {
                    setDropIndex(index);
                  }
                }}
                onDrop={e => {
                  e.preventDefault();
                  const from = dragIndex;
                  clearDrag();
                  if (from !== undefined) {
                    move(from, index);
                  }
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  {/* Laid out in the row rather than absolutely positioned into
                      a gutter the way the marker list's handle is: these rows
                      are an input and a trash, with no padding to hide a handle
                      in, and a list of at most a dozen names can spare 10px.
                      Nothing here opens on click, so it has no click to stop.

                      A one-section list draws no handle at all, for the reason
                      the marker list gives: there is nowhere to move it to. */}
                  {rows.length > 1 ? (
                    <span
                      className="custom-marker-drag-handle custom-marker-section-drag"
                      role="button"
                      tabIndex={0}
                      draggable
                      aria-label={`Reorder ${(section.name || '').trim() ||
                        'this section'}`}
                      title="Drag to reorder, or use the up and down arrow keys. This is the order your sections are grouped in, and the order they print in on a report."
                      onDragStart={e => {
                        e.dataTransfer.effectAllowed = 'move';
                        try {
                          e.dataTransfer.setData('text/plain', String(index));
                        } catch (err) {
                          // IE-ism; the drag still works off component state.
                        }
                        const line = (e.currentTarget as HTMLElement)
                          .parentElement?.parentElement;
                        if (line && e.dataTransfer.setDragImage) {
                          e.dataTransfer.setDragImage(line, 24, 16);
                        }
                        setDragIndex(index);
                        setDropIndex(undefined);
                      }}
                      onDragEnd={clearDrag}
                      onKeyDown={e => {
                        if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          moveBy(index, -1);
                        } else if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          moveBy(index, 1);
                        }
                      }}
                    />
                  ) : null}
                  <Form.Control
                    type="text"
                    className="custom-marker-input"
                    style={{
                      fontSize: '13px',
                      height: '31px',
                      maxWidth: '320px',
                    }}
                    placeholder="e.g. IgG Allergens"
                    value={section.name}
                    onChange={(e: any) => update(index, e.target.value)}
                  />
                  <Button
                    variant="link"
                    size="sm"
                    title="Remove this section"
                    style={{ color: '#766d66', padding: '0 4px' }}
                    onClick={() => {
                      // Nothing filed under it: nothing to ask about.
                      if ((counts[section.id] || 0) < 1) {
                        onRemove(section, 'move');
                        return;
                      }
                      setPending(section);
                    }}
                  >
                    <FontAwesomeIcon icon="trash-alt" />
                  </Button>
                  {/* Said before the trash is pressed, not only after: a
                      section with a panel of markers under it is not the same
                      click as an empty one. */}
                  {counts[section.id] ? (
                    <span style={{ fontSize: '12px', color: '#6c757d' }}>
                      {counts[section.id]}{' '}
                      {counts[section.id] === 1 ? 'marker' : 'markers'}
                    </span>
                  ) : null}
                </div>
                {problem ? (
                  <div style={{ fontSize: '12px', color: '#c0392b' }}>
                    {problem}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px' }}>
          You have no sections of your own yet.
        </p>
      )}

      {/* Add only. Saving is the page's one Save button: a second one here said
          "Sections", so a practitioner who had just edited a marker and reached
          for the nearest button was told "Sections saved." while their marker
          edit sat uncommitted. */}
      <Button
        variant="outline-primary"
        size="sm"
        style={{ whiteSpace: 'nowrap' }}
        onClick={() =>
          onChange([
            ...rows,
            new CustomMarkerSection({ id: newSectionId(), name: '' }),
          ])
        }
      >
        + Add a section
      </Button>

      {/* Asked rather than assumed. Deleting the section is one answer and
          keeping the definitions is the other, and which one is meant depends
          on why the section is going: a panel dropped from practice takes its
          markers with it, a section named twice by mistake must not. */}
      {pending ? (
        <Modal show onHide={() => setPending(undefined)}>
          <Modal.Header
            closeButton
            style={{ backgroundColor: '#337ab7', color: '#fff' }}
          >
            <Modal.Title style={{ fontSize: '18px' }}>
              Remove &ldquo;{pendingName}&rdquo;?
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontSize: '14px' }}>
            <p>
              {pendingCount} {pendingCount === 1 ? 'marker' : 'markers'} in your
              library {pendingCount === 1 ? 'is' : 'are'} filed under{' '}
              <strong>{pendingName}</strong>. What should happen to{' '}
              {pendingCount === 1 ? 'it' : 'them'}?
            </p>
            <ul style={{ paddingLeft: '18px', marginBottom: '12px' }}>
              <li style={{ marginBottom: '4px' }}>
                <strong>Move</strong> keeps{' '}
                {pendingCount === 1 ? 'the marker' : 'the markers'} and files{' '}
                {pendingCount === 1 ? 'it' : 'them'} under{' '}
                {CUSTOM_MARKER_OWN_SECTION_LABEL}, where you can place{' '}
                {pendingCount === 1 ? 'it' : 'them'} again later.
              </li>
              <li>
                <strong>Delete</strong> removes the section and{' '}
                {pendingCount === 1 ? 'that marker' : 'those markers'} from your
                library.
              </li>
            </ul>
            <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: 0 }}>
              Either way, tests you have already saved are left exactly as they
              are, and nothing is written until you save this page.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="link"
              style={{ color: '#6c757d' }}
              onClick={() => setPending(undefined)}
            >
              Cancel
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => {
                onRemove(pending, 'delete');
                setPending(undefined);
              }}
            >
              Delete {pendingCount === 1 ? 'the marker' : 'the markers'} too
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onRemove(pending, 'move');
                setPending(undefined);
              }}
            >
              Move to {CUSTOM_MARKER_OWN_SECTION_LABEL}
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </div>
  );
};
